/**
 * Add Inventory - Create new inventory item
 * Form with master data dropdowns loaded from API
 * 100% API-driven with NO hardcoded data
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Package, CheckCircle, AlertCircle, WifiOff } from 'lucide-react';
import {
  inventoryApi,
  type Inventory,
  type InventoryType,
  type InventoryUnit,
} from '../../../services/gaushala/api';
import { getLoggedInUserGaushalaId } from '../../../utils/auth';
import { offlineInventoryApi } from '../../../lib/offline/offlineApi';
import { offlineMasterDataApi } from '../../../lib/offline/offlineMasterData';

export default function AddInventory() {
  const navigate = useNavigate();

  // State
  const [inventoryTypes, setInventoryTypes] = useState<InventoryType[]>([]);
  const [inventoryUnits, setInventoryUnits] = useState<InventoryUnit[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [formData, setFormData] = useState({
    itemName: '',
    description: '',
    inventoryTypeId: 0,
    inventoryUnitId: 0,
    quantity: 0,
    minimumStockLevel: 0,  // Backend field name
    supplier: '',
    gaushalaId: 1,  // Default to 1
  });

  // Validation constraints
  const VALIDATION_RULES = {
    itemName: { min: 2, max: 100 },
    quantity: { min: 1, max: 9999999 },
    minimumStockLevel: { min: 1, max: 9999999 },
    supplier: { min: 0, max: 100 },
  };

  // Load master data from API
  useEffect(() => {
    loadMasterData();
    loadGaushalaId();
  }, []);

  const loadMasterData = async () => {
    try {
      // Use offline master data APIs for caching support
      const [types, units] = await Promise.all([
        offlineMasterDataApi.getAllInventoryTypes(),
        offlineMasterDataApi.getAllInventoryUnits(),
      ]);

      if (types && types.length > 0) {
        setInventoryTypes(types);
        console.log(`[AddInventory] Loaded ${types.length} inventory types`);
      } else {
        console.warn('[AddInventory] No inventory types found in cache or API');
      }

      if (units && units.length > 0) {
        setInventoryUnits(units);
        console.log(`[AddInventory] Loaded ${units.length} inventory units`);
      } else {
        console.warn('[AddInventory] No inventory units found in cache or API');
      }
    } catch (error) {
      console.error('[AddInventory] Error loading master data:', error);
    }
  };

  const loadGaushalaId = () => {
    const gaushalaId = getLoggedInUserGaushalaId();
    // Always set gaushalaId - use from token or default to 1
    setFormData(prev => ({ ...prev, gaushalaId: gaushalaId || 1 }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Item Name validation
    if (!formData.itemName.trim()) {
      newErrors.itemName = 'Please enter item name';
    } else if (formData.itemName.trim().length < VALIDATION_RULES.itemName.min) {
      newErrors.itemName = `Item name must be at least ${VALIDATION_RULES.itemName.min} characters`;
    } else if (formData.itemName.length > VALIDATION_RULES.itemName.max) {
      newErrors.itemName = `Item name cannot exceed ${VALIDATION_RULES.itemName.max} characters`;
    }

    // Inventory Type validation
    if (formData.inventoryTypeId === 0) {
      newErrors.inventoryTypeId = 'Please select inventory type';
    }

    // Unit validation
    if (formData.inventoryUnitId === 0) {
      newErrors.inventoryUnitId = 'Please select unit';
    }

    // Quantity validation - must be at least 1
    if (formData.quantity < VALIDATION_RULES.quantity.min) {
      newErrors.quantity = 'Please enter a quantity (minimum 1)';
    } else if (formData.quantity > VALIDATION_RULES.quantity.max) {
      newErrors.quantity = `Quantity cannot exceed ${VALIDATION_RULES.quantity.max}`;
    }

    // Minimum Stock Level validation - must be at least 1
    if (formData.minimumStockLevel < VALIDATION_RULES.minimumStockLevel.min) {
      newErrors.minimumStockLevel = 'Please enter minimum stock level (minimum 1)';
    } else if (formData.minimumStockLevel > VALIDATION_RULES.minimumStockLevel.max) {
      newErrors.minimumStockLevel = `Minimum stock level cannot exceed ${VALIDATION_RULES.minimumStockLevel.max}`;
    } else if (formData.minimumStockLevel > formData.quantity) {
      newErrors.minimumStockLevel = `Minimum stock level cannot be greater than initial quantity (${formData.quantity})`;
    }

    // Supplier validation (optional field, but has max length)
    if (formData.supplier && formData.supplier.length > VALIDATION_RULES.supplier.max) {
      newErrors.supplier = `Supplier name cannot exceed ${VALIDATION_RULES.supplier.max} characters`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setMessage(null);
    try {
      console.log('[Inventory] Submitting:', formData);
      const response = await inventoryApi.createInventory(formData);

      if (response.success) {
        setMessage({ type: 'success', text: 'Inventory item created successfully!' });
        setTimeout(() => navigate('/gaushala/inventory'), 1500);
      } else {
        // Check if error indicates server is unavailable
        const errorText = response.error || '';
        const isServerError = errorText.includes('500') ||
                             errorText.includes('503') ||
                             errorText.includes('status: 500') ||
                             errorText.includes('Internal Server Error') ||
                             !navigator.onLine;

        if (isServerError) {
          console.log('📴 [INVENTORY] Server error detected - attempting offline save');

          const inventoryDataForOffline = {
            gaushalaId: formData.gaushalaId,
            itemName: formData.itemName,
            description: formData.description,
            inventoryTypeId: formData.inventoryTypeId,
            inventoryUnitId: formData.inventoryUnitId,
            quantity: formData.quantity,
            minimumStockLevel: formData.minimumStockLevel,
            supplier: formData.supplier,
          };

          const offlineResult = await offlineInventoryApi.create(inventoryDataForOffline);
          if (offlineResult.success) {
            setMessage({
              type: 'success',
              text: '📴 Server unavailable - Inventory saved locally! Will sync when online.'
            });
            setTimeout(() => navigate('/gaushala/inventory'), 2500);
            return;
          }
        }

        setMessage({ type: 'error', text: 'Failed to create inventory item: ' + response.error });
      }
    } catch (error) {
      console.error('Error creating inventory:', error);

      // Try offline save on network error
      console.log('📴 [INVENTORY] Network error - attempting offline save');
      try {
        const inventoryDataForOffline = {
          gaushalaId: formData.gaushalaId || getLoggedInUserGaushalaId() || 1,
          itemName: formData.itemName,
          description: formData.description,
          inventoryTypeId: formData.inventoryTypeId,
          inventoryUnitId: formData.inventoryUnitId,
          quantity: formData.quantity,
          minimumStockLevel: formData.minimumStockLevel,
          supplier: formData.supplier,
        };

        const offlineResult = await offlineInventoryApi.create(inventoryDataForOffline);
        if (offlineResult.success) {
          setMessage({
            type: 'success',
            text: '📴 You are offline - Inventory saved locally! Will sync when online.'
          });
          setTimeout(() => navigate('/gaushala/inventory'), 2500);
          return;
        }
      } catch (offlineError) {
        console.error('Offline save also failed:', offlineError);
      }

      setMessage({ type: 'error', text: 'Error creating inventory item' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof typeof formData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/gaushala/inventory')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft size={20} />
          Back to Inventory
        </button>
        <div className="flex items-center gap-3">
          <div className="rounded-full bg-blue-100 p-3">
            <Package className="text-blue-600" size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Add Inventory Item</h1>
            <p className="text-sm text-gray-500">Create a new inventory item</p>
          </div>
        </div>
      </div>

      {/* Success/Offline Message */}
      {message && (
        <div className={`mb-6 p-4 rounded-lg border flex items-start gap-3 ${
          message.type === 'success'
            ? 'bg-green-50 border-green-200'
            : message.type === 'info'
            ? 'bg-blue-50 border-blue-200'
            : 'bg-red-50 border-red-200'
        }`}>
          {message.type === 'success' ? (
            message.text.includes('offline') || message.text.includes('📴') ? (
              <WifiOff className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            ) : (
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
            )
          ) : message.type === 'info' ? (
            <WifiOff className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          )}
          <p className={`text-sm ${
            message.type === 'success' ? 'text-green-700' :
            message.type === 'info' ? 'text-blue-700' : 'text-red-700'
          }`}>{message.text}</p>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-6">
        {/* Item Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Item Name <span className="text-red-500">*</span>
            <span className="text-xs text-gray-500 font-normal ml-2">({formData.itemName.length}/{VALIDATION_RULES.itemName.max})</span>
          </label>
          <input
            type="text"
            value={formData.itemName}
            onChange={(e) => {
              if (e.target.value.length <= VALIDATION_RULES.itemName.max) {
                handleChange('itemName', e.target.value);
              }
            }}
            maxLength={VALIDATION_RULES.itemName.max}
            className={`w-full rounded-lg border px-4 py-2 focus:ring-2 focus:border-transparent ${
              errors.itemName ? 'border-red-500 bg-red-50 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
            }`}
            placeholder="Enter item name (min 2 characters)"
            required
          />
          {errors.itemName && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <span>⚠️</span>
              {errors.itemName}
            </p>
          )}
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter item description (optional)"
            rows={3}
          />
        </div>

        {/* Inventory Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Inventory Type <span className="text-red-500">*</span>
          </label>
          {inventoryTypes.length === 0 ? (
            <div className="text-sm text-gray-500">Loading types...</div>
          ) : (
            <>
              <select
                value={formData.inventoryTypeId}
                onChange={(e) => handleChange('inventoryTypeId', parseInt(e.target.value))}
                className={`w-full rounded-lg border px-4 py-2 focus:ring-2 focus:border-transparent ${
                  errors.inventoryTypeId ? 'border-red-500 bg-red-50 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                }`}
                required
              >
                <option value={0}>Select inventory type</option>
                {inventoryTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
              {errors.inventoryTypeId && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <span>⚠️</span>
                  {errors.inventoryTypeId}
                </p>
              )}
            </>
          )}
        </div>

        {/* Unit */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Unit <span className="text-red-500">*</span>
          </label>
          {inventoryUnits.length === 0 ? (
            <div className="text-sm text-gray-500">Loading units...</div>
          ) : (
            <>
              <select
                value={formData.inventoryUnitId}
                onChange={(e) => handleChange('inventoryUnitId', parseInt(e.target.value))}
                className={`w-full rounded-lg border px-4 py-2 focus:ring-2 focus:border-transparent ${
                  errors.inventoryUnitId ? 'border-red-500 bg-red-50 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                }`}
                required
              >
                <option value={0}>Select unit</option>
                {inventoryUnits.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.unitName} {unit.abbreviation ? `(${unit.abbreviation})` : ''}
                  </option>
                ))}
              </select>
              {errors.inventoryUnitId && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <span>⚠️</span>
                  {errors.inventoryUnitId}
                </p>
              )}
            </>
          )}
        </div>

        {/* Quantity */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Initial Quantity <span className="text-red-500">*</span>
            <span className="text-xs text-gray-500 font-normal ml-2">(Max: {VALIDATION_RULES.quantity.max})</span>
          </label>
          <input
            type="text"
            inputMode="numeric"
            value={formData.quantity === 0 ? '' : formData.quantity}
            onChange={(e) => {
              const inputValue = e.target.value;
              // Only allow numeric input, reject decimals and special characters
              const numericValue = inputValue.replace(/[^0-9]/g, '');

              if (numericValue === '') {
                handleChange('quantity', 0);
              } else {
                // parseInt automatically removes leading zeros: "05" becomes 5, "0100" becomes 100
                const numValue = parseInt(numericValue, 10);
                // Enforce max length of 7 digits (max value: 9999999)
                if (numericValue.length <= 7 && numValue <= VALIDATION_RULES.quantity.max) {
                  handleChange('quantity', numValue);
                }
              }
            }}
            className={`w-full rounded-lg border px-4 py-2 focus:ring-2 focus:border-transparent ${
              errors.quantity ? 'border-red-500 bg-red-50 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
            }`}
            placeholder="Enter quantity (1-9999999)"
            required
          />
          {errors.quantity && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <span>⚠️</span>
              {errors.quantity}
            </p>
          )}
        </div>

        {/* Minimum Stock Level */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Minimum Stock Level <span className="text-red-500">*</span>
            {formData.quantity > 0 && (
              <span className="text-xs text-gray-500 font-normal ml-2">(Max: {formData.quantity})</span>
            )}
          </label>
          <input
            type="text"
            inputMode="numeric"
            value={formData.minimumStockLevel === 0 ? '' : formData.minimumStockLevel}
            onChange={(e) => {
              const inputValue = e.target.value;
              // Only allow numeric input, reject decimals and special characters
              const numericValue = inputValue.replace(/[^0-9]/g, '');

              if (numericValue === '') {
                handleChange('minimumStockLevel', 0);
              } else {
                // parseInt automatically removes leading zeros: "05" becomes 5, "0100" becomes 100
                const numValue = parseInt(numericValue, 10);
                // Enforce max length of 7 digits (max value: 9999999)
                if (numericValue.length <= 7 && numValue <= VALIDATION_RULES.minimumStockLevel.max) {
                  handleChange('minimumStockLevel', numValue);
                }
              }
            }}
            className={`w-full rounded-lg border px-4 py-2 focus:ring-2 focus:border-transparent ${
              errors.minimumStockLevel ? 'border-red-500 bg-red-50 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
            }`}
            placeholder="Enter minimum stock level (1-9999999)"
            required
          />
          {errors.minimumStockLevel && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <span>⚠️</span>
              {errors.minimumStockLevel}
            </p>
          )}
          <p className="mt-1 text-sm text-gray-500">
            Alert will be shown when quantity falls below this level
          </p>
        </div>

        {/* Supplier (Optional) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Supplier
            <span className="text-xs text-gray-500 font-normal ml-2">({formData.supplier.length}/{VALIDATION_RULES.supplier.max})</span>
          </label>
          <input
            type="text"
            value={formData.supplier}
            onChange={(e) => {
              if (e.target.value.length <= VALIDATION_RULES.supplier.max) {
                handleChange('supplier', e.target.value);
              }
            }}
            maxLength={VALIDATION_RULES.supplier.max}
            className={`w-full rounded-lg border px-4 py-2 focus:ring-2 focus:border-transparent ${
              errors.supplier ? 'border-red-500 bg-red-50 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
            }`}
            placeholder="Enter supplier name (optional, max 100 characters)"
          />
          {errors.supplier && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <span>⚠️</span>
              {errors.supplier}
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 pt-4">
          <button
            type="submit"
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Creating...
              </>
            ) : (
              <>
                <Save size={20} />
                Create Inventory Item
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => navigate('/gaushala/inventory')}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}